// LastInReport.tsx
import React, { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
// import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { GridRenderCellParams } from '@mui/x-data-grid';
import LocalizedGrid, { CustomGridColDef } from '../../localizedGrid/LocalizedGridCook3h';
import dayjs, { Dayjs } from "dayjs";
import { useFetch } from '../../../hooks/useFetch2.ts';
import { Event, reportApi } from './api/api.ts';
import { Group } from '../../../types/group';
import { api as groupApi } from "../../../api/groupApi";
import { api as entityApi } from '../../../api/entityApi';
import { Subtenant, subtenantApi } from '../../../api/subtenantApi';
import { LocalizedDatePicker } from '../../localizedDatePicker/LocalizedDatePicker.tsx';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n.ts';
import { Entity } from '../../../types/entity.ts';

interface TransportRow {
    // id?: string | number; 
    ts_start: string; // timestamptz приходит в виде ISO-строки
    registration_number: string;
    matched_number: string;
    is_enter: boolean;
    customer_id: number;
    car_owner: string;
    group_id: number;
    entity_id_1: number; // Наше целевое поле
    subtenant_id: number;
}

export const LastInReport: React.FC = () => {
    const { t } = useTranslation();

    const [from, setFrom] = useState<Dayjs | null>(dayjs().startOf("day"));
    const [ period_days, setPeriod_days] = useState<number>(1);
    const { data, setData, error, update, loading } = useFetch<Event[]>(reportApi.getEventLastIn, [], { moment: from?.toISOString(), period_days, });
    const { data: groups, } = useFetch<Group[]>(groupApi.get, [], );
    const { data: subtenants } = useFetch<Subtenant[]>(subtenantApi.get, []);
    const { data: entities = [], loading: entityLoading } = useFetch<Entity[]>(entityApi.get, [], { type: 1 });

    const columns: CustomGridColDef[] = [
        { field: 'id', headerName: 'ID', width: 90 },
        { field: 'ts_start', headerName: 'Момент', width: 100, },
        { field: 'matched_number', headerName: 'matched_number', width: 180, },
        { field: 'registration_number', headerName: 'registration_number', width: 180, },
        { field: 'car_brand', headerName: 'car_brand', width: 180, },
        { field: 'car_owner', headerName: 'car_owmer', width: 180, },
        { field: "group_id", headerName: t('PlatesList.group'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: groups.map( group => ({value: group.group_id, label: group.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<Group>) => {
                return (
                    <Typography>
                        {groups.find(g => g.group_id === params.row.group_id)?.name}
                    </Typography>
                );
            }
        },  
        { field: "entity_id_1", headerName: t('PlatesList.location'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: entities.map( e => ({value: e.entity_id, label: e.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<TransportRow>) => {
                return (
                    <Typography>
                        {entities.find(e => e.entity_id === params.row.entity_id_1)?.name}
                    </Typography>
                );
            }
        },
        { field: "subtenant_id", headerName: t('PlatesList.subtenant'), type: "singleSelect", flex: 2, width: 100, display: 'flex',
            valueOptions: subtenants.map( subtenant => ({value: subtenant.subtenant_id, label: subtenant.name})),
            editable: false,
            // hidden: onlyOwn || !!subtenantId,
            renderCell: (params: GridRenderCellParams<Subtenant>) => {
                return (
                    <Typography>
                        {subtenants.find(s => s.subtenant_id === params.row.subtenant_id)?.name}
                    </Typography>
                );
            }
        },

        { field: 'customer_id', headerName: 'customer_id', width: 180, },
    ];

    const handleRefresh = () => {
        setData([]);
        update({ moment: from?.toISOString(), period_days });
    };

    return (
        <Box sx={{ width: '100%', p: 2 }}>

            <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold' }}>
                {/* Звіт про подію останнього прибуття */}
            </Typography>

            {/* Контейнер для панели инструментов */}
            <Box 
                sx={{ 
                    display: 'flex', 
                    gap: 2,             // Отступы между элементами
                    alignItems: 'center', // Выравнивание по вертикали (чтобы все были на одной линии)
                    mb: 3,              // Отступ снизу до таблицы
                    flexWrap: 'wrap'    // На случай узких экранов
                }}
            >
                <LocalizedDatePicker
                    value={from}
                    onChange={(v) => setFrom(v)}
                    slotProps={{ textField: { size: 'small' } }} // Делаем высоту компактной
                    sx={{ width: 200 }}
                />
                
                <TextField
                    label="Період"
                    type="number"
                    size="small" // Задаем ту же высоту, что и у DatePicker
                    value={period_days}
                    onChange={(e) => {
                        const val = parseInt(e.target.value, 10);
                        setPeriod_days(isNaN(val) ? 0 : val);
                    }}
                    sx={{ width: 120 }} // Ограничиваем ширину, чтобы он не растягивался
                />

                <Button
                    variant="contained"
                    onClick={handleRefresh}
                    size="medium" // Чуть больше, чтобы идеально встать вровень с size="small" инпутов
                    sx={{ height: 40 }} // Либо явно фиксируем высоту кнопки под инпуты
                >
                    Оновити {/* или твой перевод {('refresh')} */}
                </Button>
            </Box>
            
            <LocalizedGrid
                lang={i18n.language as any}
                gridId='LastInReportGrid'
                getRowId={(row: Event) => `${row.ts_start}_${row.registration_number}` }
                rows={data || []}
                columns={columns}
                pageSizeOptions={[10, 25, 100]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                disableRowSelectionOnClick
                autoHeight // Чтобы сетка не сжималась и подстраивалась под контент
                loading={loading}
            />
        </Box>
    )
}
